import {useDataContext} from "../../SurveyContextProvider";
import {getInstanceConfigValue, getAuthConfig} from "../../../utils";
import {useIntl} from "react-intl";
import {AlertCircle, AlertTriangle} from "lucide-react";
import {useLanguageData} from "../../../App";
import {useEffect, useMemo} from "react";
import {AutoResizeTextarea} from "../../../UIElements";
import "./WhatsappMessage.scss"
import {useVariableValidation} from "../hooks/useVariableValidation";
import {useQueries} from "react-query";
import axios from "axios";

const auth = getAuthConfig();

export default function WhatsappMessage() {
    const intl = useIntl()
    const { value: instanceConfig } = useDataContext('instanceConfig')
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData');

    // Get array of template names from instance config
    const configWhatsAppTemplates = getInstanceConfigValue("ui", "whatsappTemplates", instanceConfig)
    const fallbackTemplate = getInstanceConfigValue("whatsapp", "template", instanceConfig)

    const whatsAppTemplates = configWhatsAppTemplates?.length > 0 ? configWhatsAppTemplates : fallbackTemplate ? [fallbackTemplate] : []

    const selectedTemplate = surveyData?.config?.whatsappTemplate || fallbackTemplate

    // Fetch all templates at once using useQueries
    const templateQueries = useQueries(
        (whatsAppTemplates && Array.isArray(whatsAppTemplates) ? whatsAppTemplates : []).map(templateName => ({
            queryKey: ['whatsapp', templateName],
            queryFn: async () => {
                const { data } = await axios.get(
                    `/api/v2/translation.php/whatsapp/${templateName}`,
                    auth
                )
                return data
            },
            enabled: !!templateName
        }))
    )

    // Check loading and error states
    const isLoading = templateQueries.some(q => q.isLoading)
    const hasError = templateQueries.some(q => q.isError)
    const allSuccess = templateQueries.length > 0 && templateQueries.every(q => q.isSuccess)

    // Combine all template data into object
    const allTemplatesData = useMemo(() => {
        if (!allSuccess || !whatsAppTemplates) return {}
        const result = {}
        whatsAppTemplates.forEach((name, index) => {
            result[name] = templateQueries[index].data
        })
        return result
    }, [templateQueries, allSuccess, whatsAppTemplates])

    useEffect(() => {
        if (!surveyData?.config?.whatsappTemplate && whatsAppTemplates?.length > 0) {
            setSurveyData({
                ...surveyData,
                config: {
                    ...surveyData.config,
                    whatsappTemplate: whatsAppTemplates[0]
                }
            }, false)
        }
    }, [surveyData, setSurveyData, whatsAppTemplates])

    if (!whatsAppTemplates || !Array.isArray(whatsAppTemplates) || whatsAppTemplates.length === 0 || hasError) {
        return <div className={"error"}>
            <AlertTriangle size={20}/>
            {intl.formatMessage({
                id:'whatsapp.config.missing',
                defaultMessage: 'The WhatsApp message template is not configured correctly. Please contact the Annie team for assistance.'
            })}
        </div>
    }

    if (isLoading) {
        return <div className={"loading"}>
            {intl.formatMessage({
                id:'whatsapp.config.loading',
                defaultMessage: 'Loading the WhatsApp message template...'
            })}
        </div>
    }

    if (allSuccess) {
        return <WhatsappMessageEditor
            templates={allTemplatesData[selectedTemplate]}
            allTemplates={allTemplatesData}
            whatsAppTemplates={whatsAppTemplates}
            selectedTemplate={selectedTemplate}
        />
    }

    return null
}

function WhatsappMessageEditor({templates, allTemplates, whatsAppTemplates, selectedTemplate}) {
    const intl = useIntl()
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData');
    const { value: contactMetaArray } = useDataContext('contactMetaArray')
    const { instanceLang } = useLanguageData()
    const surveyLanguage = surveyData?.config?.language || instanceLang
    const template = templates?.find(obj => obj?.language === surveyLanguage)
    const templateContents = template?.value

    const handleTemplateChange = (newTemplate) => {
        setSurveyData({
            ...surveyData,
            config: {
                ...surveyData.config,
                whatsappTemplate: newTemplate,
            }
        })
    }

    // Extract variables from template
    const extractVariables = (text) => {
        if (!text) return []
        const regex = /{{(\w+)}}/g
        const matches = [...text.matchAll(regex)]
        return [...new Set(matches.map(match => match[1]))]
    }

    // Get variables excluding those in contactMetaArray
    const templateVariables = extractVariables(templateContents)
        .filter(variable => !contactMetaArray?.includes(variable))

    useEffect(() => {
        if (templateContents && surveyData?.config?.message !== templateContents) {
            setSurveyData({
                ...surveyData,
                config: {
                    ...surveyData.config,
                    message: templateContents,
                    whatsappVariables: surveyData?.config?.whatsappVariables || {}
                }
            }, false)
        }
    }, [templateContents, setSurveyData, surveyData])

    useEffect(() => {
        if (templateVariables && templateVariables.length > 0) {
            const currentVariables = surveyData?.config?.whatsappVariables || {}
            const needsUpdate = templateVariables.some(variable => !(variable in currentVariables))

            if (needsUpdate) {
                const updatedVariables = { ...currentVariables }
                templateVariables.forEach(variable => {
                    if (!(variable in updatedVariables)) {
                        updatedVariables[variable] = ''
                    }
                })

                setSurveyData({
                    ...surveyData,
                    config: {
                        ...surveyData.config,
                        whatsappVariables: updatedVariables
                    }
                }, false)
            }
        }
    }, [templateVariables, surveyData, setSurveyData])

    const handleVariableChange = (variable, value) => {
        setSurveyData({
            ...surveyData,
            config: {
                ...surveyData.config,
                whatsappVariables: {
                    ...surveyData?.config?.whatsappVariables,
                    [variable]: value
                }
            }
        })
    }

    const filledTemplate = useMemo(() => {
        if (!templateContents) return null

        // Split the template into parts, keeping the delimiters
        const parts = templateContents.split(/({{[^}]+}})/g)

        return parts.map((part, index) => {
            // Check if this part is a variable
            const variableMatch = part.match(/^{{(\w+)}}$/)

            if (variableMatch) {
                const variable = variableMatch[1]
                const value = surveyData?.config?.whatsappVariables?.[variable]

                // If it's a contactMeta variable
                if (contactMetaArray?.includes(variable)) {
                    return (
                        <SimpleVariableComponent
                            key={index}
                            variable={variable}
                        />
                    )
                }

                // If it has a user-provided value
                if (value) {
                    return <span key={index} className="whatsapp-variable">{value}</span>
                }

                // Empty variable that's not in contactMeta
                return <span key={index}
                             className="whatsapp-variable value-missing"
                             title={intl.formatMessage({
                                 defaultMessage: 'Enter text for variable',
                                 id: 'enterVariableValue'
                             })}>
                    {variable}
                </span>
            }

            // Regular text
            return <span key={index}>{part}</span>
        })
    }, [templateContents, surveyData?.config?.whatsappVariables, contactMetaArray])

    if (!template) {
        return <>
            <TemplateSelector
                selectedTemplate={selectedTemplate}
                handleTemplateChange={handleTemplateChange}
                whatsAppTemplates={whatsAppTemplates}
                intl={intl}
            />
            <div className={"error"}>
                <AlertCircle size={20}/>
                {intl.formatMessage({
                    id:'whatsapp.config.translation.missing',
                    defaultMessage: 'The WhatsApp message template is not in the selected survey language. Please change the survey language or contact the Annie team for assistance.'
                })}
            </div>
        </>
    }

    return <>
        <TemplateSelector
            selectedTemplate={selectedTemplate}
            handleTemplateChange={handleTemplateChange}
            whatsAppTemplates={whatsAppTemplates}
            intl={intl}
        />
        <div className={"whatsapp-editor"}>
            <div className={"template-text"}>
                {filledTemplate}
            </div>
            <div className={"template-inputs"}>
                {templateVariables.map(variable => (
                    <div key={variable} className="template-input-wrapper">
                        <label htmlFor={`var-${variable}`}>
                            {variable}
                        </label>
                        <AutoResizeTextarea
                            id={`var-${variable}`}
                            className={"variable-input"}
                            value={surveyData?.config?.whatsappVariables?.[variable] || ''}
                            onChange={(e) => handleVariableChange(variable, e.target.value)}
                            placeholder={intl.formatMessage({
                                defaultMessage: 'Enter text for variable',
                                id: 'enterVariableValue'
                            })}
                            rows={1}
                            disableLinebreaks={true}
                        />
                    </div>
                ))}
            </div>
        </div>
    </>
}

const TemplateSelector = ({selectedTemplate, handleTemplateChange, whatsAppTemplates, intl}) => {
    return <div className={"template-selector"}>
        <select
            id="whatsapp-template-select"
            value={selectedTemplate}
            onChange={(e) => handleTemplateChange(e.target.value)}
        >
            {whatsAppTemplates.map(templateName => (
                <option key={templateName} value={templateName}>
                    {intl.formatMessage({
                        id: `whatsapp.template.${templateName}`,
                        defaultMessage: templateName
                    })}
                </option>
            ))}
        </select>
    </div>
}

export const SimpleVariableComponent = ({ variable }) => {
    const { variableClass, variableTitle, variableDisplayName } = useVariableValidation(variable)

    return <span className={`contact-variable ${variableClass}`} title={variableTitle}>
      {variableDisplayName}
    </span>
}